// LocalStorage access of the scanner.

function read(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage unavailable — the value just won't persist
  }
}

function remove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // storage unavailable — nothing to remove
  }
}

// ---------------------------------------------------------------------------
// Completed scans of a multiple-scan order (awaiting the combined upload),
// keyed per order so a page refresh doesn't lose them.

function completedScansKey(labId, webOrderId) {
  return `snugfit.multiScan.${labId}.${webOrderId}`;
}

export function loadCompletedScans(labId, webOrderId) {
  const raw = read(completedScansKey(labId, webOrderId));
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function saveCompletedScans(labId, webOrderId, scans) {
  write(completedScansKey(labId, webOrderId), JSON.stringify(scans));
}

// forget the order's scans (e.g. once the combined upload succeeded).
export function clearCompletedScans(labId, webOrderId) {
  remove(completedScansKey(labId, webOrderId));
}

// ---------------------------------------------------------------------------
// The user's scan settings (camera facing, torch, contour mode, timers),
// restored when a new scan starts. Kept per body part (under `bodyParts`), as
// different scans may use different settings.

const SCAN_SETTINGS_KEY = "snugfit.scanSettings";

function loadScanSettings() {
  const raw = read(SCAN_SETTINGS_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

// the settings remembered for one body part (case-insensitive).
export function loadBodyPartSettings(bodyPart) {
  return loadScanSettings().bodyParts?.[bodyPart.toLowerCase()] || {};
}

export function saveBodyPartSetting(bodyPart, name, value) {
  const all = loadScanSettings();
  const key = bodyPart.toLowerCase();
  const bodyParts = all.bodyParts || {};
  write(
    SCAN_SETTINGS_KEY,
    JSON.stringify({
      ...all,
      bodyParts: { ...bodyParts, [key]: { ...bodyParts[key], [name]: value } },
    }),
  );
}

// ---------------------------------------------------------------------------
// The user's app settings (currently just the upload file format), chosen in
// the dashboard's Settings dialog and restored on the next visit.

const APP_SETTINGS_KEY = "snugfit.appSettings";

export function loadAppSettings() {
  const raw = read(APP_SETTINGS_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function saveAppSetting(name, value) {
  write(
    APP_SETTINGS_KEY,
    JSON.stringify({ ...loadAppSettings(), [name]: value }),
  );
}

// ---------------------------------------------------------------------------
// The per-body-part "don't show the tutorial again" choice.

function tutorialHiddenKey(bodyPart) {
  return `snugfit.hideTutorial.${bodyPart.toLowerCase()}`;
}

export function isTutorialHidden(bodyPart) {
  return read(tutorialHiddenKey(bodyPart)) === "1";
}

export function setTutorialHidden(bodyPart) {
  write(tutorialHiddenKey(bodyPart), "1");
}
